import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UtensilsCrossed, Loader2, ArrowRight, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Signup() {
  const [form, setForm] = useState({ business_name: "", name: "", email: "", password: "", slug: "", sales_code: "", website: "" });
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const slugify = (t) => t.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 48);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/public/signup", form);
      await login(form.email, form.password);
      if (r.data.trial) {
        toast.success("Welcome! Your 14-day full-access trial is ready — pre-filled with demo data you can clear anytime.");
      } else {
        toast.success("Account created — welcome aboard.");
      }
      navigate("/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not create your account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="signup-page">
      <div className="hidden w-1/2 flex-col justify-between bg-ink p-12 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center bg-brand"><UtensilsCrossed className="h-5 w-5 text-white" /></div>
          <span className="font-heading text-lg font-bold tracking-tight" data-testid="signup-brand">Catering Diary</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight" data-testid="signup-title">
            Run your catering business from one diary.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
            Enquiries, quotes, bookings, staffing, equipment and your own public ordering hub — set up in minutes.
          </p>
          <div className="mt-6 flex items-center gap-2 border border-white/15 bg-white/5 px-4 py-3">
            <PartyPopper className="h-4 w-4 shrink-0 text-brand" />
            <p className="text-xs text-white/70">Have a sales team code? Add it below for a 14-day full-access trial pre-filled with demo data.</p>
          </div>
        </div>
        <p className="text-xs text-white/40">CateringDiary.app</p>
      </div>

      <div className="flex flex-1 items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <p className="font-heading text-xl font-bold" data-testid="signup-brand-mobile">Catering Diary</p>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight" data-testid="signup-heading">Create your account</h2>
          <p className="mt-1 text-sm text-muted-foreground">Free to start. No card required.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label htmlFor="business">Business name</Label>
              <Input id="business" data-testid="signup-business-input" required value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value, slug: slugTouched ? form.slug : slugify(e.target.value) })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slug">Hub URL</Label>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">/hub/</span>
                <Input id="slug" data-testid="signup-slug-input" required value={form.slug}
                  onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" data-testid="signup-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" data-testid="signup-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password (min 8 chars)</Label>
              <Input id="password" type="password" data-testid="signup-password-input" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">Sales team code <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="code" data-testid="signup-code-input" placeholder="e.g. CD-AB12CD" value={form.sales_code}
                onChange={(e) => setForm({ ...form, sales_code: e.target.value.toUpperCase() })} />
            </div>
            {/* honeypot */}
            <input type="text" name="website" data-testid="signup-honeypot" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1} autoComplete="off" className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />
            {error && <p className="text-sm font-medium text-red-600" data-testid="signup-error">{error}</p>}
            <Button type="submit" data-testid="signup-submit-button" disabled={loading}
              className="w-full rounded-md bg-ink font-heading font-bold hover:bg-ink-light">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              Create account
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" data-testid="signup-login-link" className="font-semibold text-brand hover:text-brand-dark">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
